import type { FormEvent, ReactNode } from "react";
import { site } from "../../siteConfig";
import type { HeroSection } from "../../content/industry";
import { Icon } from "./Icon";
import { CountUp } from "./Motion";
import { Photo } from "./Photo";

function Actions({ section }: { section: HeroSection }) {
  return (
    <div className="ix-actions">
      <a className="ix-button ix-button--primary" href={section.primary.href}>{section.primary.label}</a>
      {section.secondary ? <a className="ix-button ix-button--ghost" href={section.secondary.href}>{section.secondary.label}</a> : null}
    </div>
  );
}

function Copy({ section, note = true }: { section: HeroSection; note?: boolean }) {
  return (
    <>
      <p className="ix-eyebrow">{section.eyebrow}</p>
      <h1 id="hero-title">{section.title}</h1>
      <p className="ix-lead">{section.text}</p>
      <Actions section={section} />
      {note && section.note ? <p className="ix-note">{section.note}</p> : null}
    </>
  );
}

function Stats({ section }: { section: HeroSection }) {
  return section.stats?.length ? <dl className="ix-stats">{section.stats.map(stat => <div key={stat.label}><dt>{stat.label}</dt><dd><CountUp value={stat.value} /></dd></div>)}</dl> : null;
}

/** Sellos de confianza que flotan sobre la fotografía (opiniones, certificaciones, disponibilidad). */
function Chips({ section }: { section: HeroSection }) {
  return section.chips?.length ? <ul className="ix-hero-chips">{section.chips.map(chip => <li key={chip.label}>{chip.icon ? <Icon name={chip.icon} size={16} className={chip.icon === "star" ? "ix-chip-icon ix-star" : "ix-chip-icon"} /> : null}{chip.label}</li>)}</ul> : null;
}

const KEYWORDS = ["const", "let", "return", "new", "class", "public", "private", "async", "await", "import", "export", "from", "function", "true", "false", "null"];
const TOKEN = new RegExp(`(\\/\\/.*$|"[^"]*"|\\b(?:${KEYWORDS.join("|")})\\b|\\b\\d+(?:\\.\\d+)?\\b)`);
/** Resaltado mínimo para la ventana de código: comentarios, cadenas, palabras reservadas y números. */
function highlight(line: string) {
  return line.split(TOKEN).filter(Boolean).map((part, index) => {
    const kind = part.startsWith("//") ? "comment" : part.startsWith("\"") ? "string" : /^\d/.test(part) ? "number" : KEYWORDS.includes(part) ? "keyword" : "";
    return kind ? <span key={index} className={`ix-tok-${kind}`}>{part}</span> : part;
  });
}

/** Ventana de editor con un fragmento de código en lugar de fotografía (portafolios de desarrollo). */
function CodeWindow({ code }: { code: NonNullable<HeroSection["code"]> }) {
  return (
    <figure className="ix-code-window" aria-label={`Fragmento de código de ${code.file}`}>
      <div className="ix-code-bar" aria-hidden="true"><span /><span /><span /><em>{code.file}</em></div>
      <pre><code>{code.lines.map((line, index) => <span key={index} className="ix-code-line">{highlight(line)}{"\n"}</span>)}</code></pre>
    </figure>
  );
}

function Shell({ section, children }: { section: HeroSection; children: ReactNode }) {
  return <section id="inicio" className={`ix-hero ix-hero--${section.variant}`} aria-labelledby="hero-title">{children}</section>;
}

/** Portada dividida: texto e indicadores a la izquierda, fotografía a la derecha (industria, B2B). */
export function HeroSplit({ section }: { section: HeroSection }) {
  return (
    <Shell section={section}>
      <div className="ix-container ix-hero-grid">
        <div className="ix-hero-copy"><Copy section={section} note={false} /></div>
        <div className="ix-hero-media"><Photo index={section.images[0] ?? 0} priority sizes="(min-width: 1024px) 50vw, 100vw" /><Chips section={section} /></div>
      </div>
      {section.stats?.length ? <div className="ix-container"><Stats section={section} />{section.note ? <p className="ix-note">{section.note}</p> : null}</div> : null}
    </Shell>
  );
}

/** Portada editorial asimétrica: titular serif con dos fotografías verticales desfasadas (belleza, moda). */
export function HeroEditorial({ section }: { section: HeroSection }) {
  return (
    <Shell section={section}>
      <div className="ix-container ix-hero-grid">
        <div className="ix-hero-copy"><Copy section={section} /></div>
        <div className="ix-hero-media">
          <Photo index={section.images[0] ?? 0} priority className="ix-hero-main" sizes="(min-width: 1024px) 34vw, 70vw" />
          {section.images[1] !== undefined ? <Photo index={section.images[1]} className="ix-hero-accent" sizes="(min-width: 1024px) 16vw, 40vw" /> : null}
          <p className="ix-hero-tag">{site.tagline}</p>
        </div>
      </div>
    </Shell>
  );
}

/** Pantalla completa: fotografía de fondo, titular centrado y reserva destacada (restaurantes). */
export function HeroFullscreen({ section }: { section: HeroSection }) {
  return (
    <Shell section={section}>
      <div className="ix-hero-media"><Photo index={section.images[0] ?? 0} priority /></div>
      <div className="ix-container ix-hero-copy"><Copy section={section} /></div>
    </Shell>
  );
}

/** Enmarcada: titular serif centrado sobre una fotografía panorámica con marco fino (hotelería de lujo). */
export function HeroFramed({ section }: { section: HeroSection }) {
  return (
    <Shell section={section}>
      <div className="ix-container ix-hero-copy"><Copy section={section} note={false} /></div>
      <div className="ix-container ix-hero-frame"><Photo index={section.images[0] ?? 0} priority sizes="(min-width: 1280px) 1200px, 100vw" />{section.note ? <p className="ix-hero-caption">{section.note}</p> : null}</div>
    </Shell>
  );
}

/** Panorama: paisaje a lo ancho con el texto encima y tres postales de destinos que lo desbordan (viajes). */
export function HeroPanorama({ section }: { section: HeroSection }) {
  const postcards = section.images.slice(1, 4);
  return (
    <Shell section={section}>
      <div className="ix-hero-media"><Photo index={section.images[0] ?? 0} priority /></div>
      <div className="ix-container ix-hero-copy"><Copy section={section} /></div>
      {postcards.length ? (
        <div className="ix-container ix-postcards">
          {postcards.map((image, index) => <figure key={image} className="ix-postcard"><Photo index={image} sizes="(min-width: 1024px) 25vw, 45vw" /><figcaption>{section.captions?.[index] ?? ""}</figcaption></figure>)}
        </div>
      ) : null}
    </Shell>
  );
}

/** Minimal: titular sobredimensionado, texto a dos columnas y fotografía arquitectónica a todo el ancho (construcción). */
export function HeroMinimal({ section }: { section: HeroSection }) {
  return (
    <Shell section={section}>
      <div className="ix-container">
        <p className="ix-eyebrow">{section.eyebrow}</p>
        <h1 id="hero-title">{section.title}</h1>
        <div className="ix-hero-columns"><p className="ix-lead">{section.text}</p><div><Actions section={section} /><Stats section={section} /></div></div>
      </div>
      <div className="ix-hero-wide"><Photo index={section.images[0] ?? 0} priority />{section.note ? <p className="ix-hero-caption ix-container">{section.note}</p> : null}</div>
    </Shell>
  );
}

/** Buscador: fotografía de fondo con un panel de búsqueda; al enviarlo rellena el formulario de contacto y lleva hasta él (inmobiliaria). */
export function HeroSearch({ section }: { section: HeroSection }) {
  const search = section.search;
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!search) return;
    const data = new FormData(event.currentTarget);
    for (const field of search.fields) {
      const target = document.getElementById(`${search.target}-${field.name}`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (target) target.value = String(data.get(field.name) ?? "");
    }
    document.getElementById(search.target)?.scrollIntoView({ behavior: "smooth" });
    (document.getElementById(`${search.target}-${search.fields[0]?.name}`) as HTMLElement | null)?.focus({ preventScroll: true });
  };
  return (
    <Shell section={section}>
      <div className="ix-hero-media"><Photo index={section.images[0] ?? 0} priority /></div>
      <div className="ix-container ix-hero-copy"><Copy section={section} note={false} /></div>
      {search ? (
        <form className="ix-container ix-search" onSubmit={handleSubmit} aria-label="Buscar">
          {search.fields.map(field => <div key={field.name} className="ix-field">
            <label htmlFor={`hero-${field.name}`}>{field.label}</label>
            {field.type === "select"
              ? <select id={`hero-${field.name}`} name={field.name} defaultValue={field.options?.[0]}>{field.options?.map(option => <option key={option}>{option}</option>)}</select>
              : <input id={`hero-${field.name}`} name={field.name} type={field.type} autoComplete={field.autocomplete} />}
          </div>)}
          <button type="submit" className="ix-button ix-button--primary">{search.submitLabel}</button>
        </form>
      ) : null}
      {section.note ? <p className="ix-container ix-note ix-hero-note">{section.note}</p> : null}
    </Shell>
  );
}

/** Consola: fondo oscuro con retícula, fotografía con marcas de esquina e indicadores técnicos (automatización). */
export function HeroConsole({ section }: { section: HeroSection }) {
  return (
    <Shell section={section}>
      <div className="ix-container ix-hero-grid">
        <div className="ix-hero-copy"><Copy section={section} /></div>
        <div className="ix-hero-media ix-console">
          {section.code ? <CodeWindow code={section.code} /> : <Photo index={section.images[0] ?? 0} priority sizes="(min-width: 1024px) 50vw, 100vw" />}
          <Chips section={section} />
          {section.stats?.length ? <dl className="ix-console-readout">{section.stats.map(stat => <div key={stat.label}><dt>{stat.label}</dt><dd><CountUp value={stat.value} /></dd></div>)}</dl> : null}
        </div>
      </div>
    </Shell>
  );
}

/** Collage: tres fotografías superpuestas con pies a mano junto al titular (cafetería, artesanos). */
export function HeroCollage({ section }: { section: HeroSection }) {
  return (
    <Shell section={section}>
      <div className="ix-container ix-hero-grid">
        <div className="ix-hero-copy"><Copy section={section} /></div>
        <div className="ix-collage">
          {section.images.slice(0, 3).map((image, index) => <figure key={image} className={`ix-collage-item ix-collage-item--${index + 1}`}>
            <Photo index={image} priority sizes="(min-width: 1024px) 30vw, 60vw" />
            {section.captions?.[index] ? <figcaption>{section.captions[index]}</figcaption> : null}
          </figure>)}
        </div>
      </div>
    </Shell>
  );
}

/** Tarjeta: fotografía a la derecha y una tarjeta de cita/horario que se superpone (salud). */
export function HeroCard({ section }: { section: HeroSection }) {
  return (
    <Shell section={section}>
      <div className="ix-container ix-hero-grid">
        <div className="ix-hero-copy"><Copy section={section} /></div>
        <div className="ix-hero-media">
          <Photo index={section.images[0] ?? 0} priority sizes="(min-width: 1024px) 50vw, 100vw" />
          {section.card ? (
            <aside className="ix-hero-card" aria-label={section.card.title}>
              <h2>{section.card.title}</h2>
              <ul>{section.card.items.map(item => <li key={item}>{item}</li>)}</ul>
              <a className="ix-button ix-button--primary" href={section.card.cta.href}>{section.card.cta.label}</a>
            </aside>
          ) : null}
        </div>
      </div>
    </Shell>
  );
}

/** Declaración: fondo sólido oscuro, frase serif de gran tamaño y retrato vertical estrecho (despachos). */
export function HeroStatement({ section }: { section: HeroSection }) {
  return (
    <Shell section={section}>
      <div className="ix-container ix-hero-grid">
        <div className="ix-hero-copy"><Copy section={section} note={false} /><Stats section={section} /></div>
        <div className="ix-hero-media"><Photo index={section.images[0] ?? 0} priority sizes="(min-width: 1024px) 30vw, 100vw" />{section.note ? <p className="ix-hero-caption">{section.note}</p> : null}</div>
      </div>
    </Shell>
  );
}

/** Centrada: propuesta de valor al centro y una maqueta de la aplicación dibujada con CSS (software). */
export function HeroCentered({ section }: { section: HeroSection }) {
  const mock = section.mock;
  return (
    <Shell section={section}>
      <div className="ix-container ix-hero-copy"><Copy section={section} /></div>
      <div className="ix-container">
        {mock ? (
          <div className="ix-mock" role="img" aria-label={`Vista de ejemplo de ${mock.title}`}>
            <div className="ix-mock-bar"><span /><span /><span /><strong>{mock.title}</strong></div>
            <div className="ix-mock-body">
              <div className="ix-mock-side">{mock.rows.map((row, index) => <span key={row} className={index === 0 ? "is-active" : ""}>{row}</span>)}</div>
              <div className="ix-mock-main">
                <div className="ix-mock-metrics">{mock.metrics.map(metric => <div key={metric.label}><small>{metric.label}</small><b>{metric.value}</b></div>)}</div>
                <div className="ix-mock-chart">{[38, 52, 45, 64, 58, 72, 69, 84, 78, 92].map((height, index) => <i key={index} style={{ height: `${height}%` }} />)}</div>
              </div>
            </div>
          </div>
        ) : <div className="ix-hero-media"><Photo index={section.images[0] ?? 0} priority /></div>}
      </div>
    </Shell>
  );
}

export function Hero({ section }: { section: HeroSection }) {
  switch (section.variant) {
    case "editorial": return <HeroEditorial section={section} />;
    case "fullscreen": return <HeroFullscreen section={section} />;
    case "framed": return <HeroFramed section={section} />;
    case "panorama": return <HeroPanorama section={section} />;
    case "minimal": return <HeroMinimal section={section} />;
    case "search": return <HeroSearch section={section} />;
    case "console": return <HeroConsole section={section} />;
    case "collage": return <HeroCollage section={section} />;
    case "card": return <HeroCard section={section} />;
    case "statement": return <HeroStatement section={section} />;
    case "centered": return <HeroCentered section={section} />;
    default: return <HeroSplit section={section} />;
  }
}
